import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Lightbulb,
  Database,
  BrainCircuit,
  Sparkles,
  Blocks,
  MonitorSmartphone,
  ShieldCheck,
  FlaskConical,
  LifeBuoy,
  Handshake,
  ArrowRight,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import SEO, { buildBreadcrumbJsonLd } from "@/components/SEO";
import { seoConfig } from "@/data/seoConfig";

const offerings = [
  {
    icon: Lightbulb,
    image: "/assets/Use_Case.jpg",
    title: "Ideation and Use Case Identification",
    bullets: [
      ["Collaborative AI Ideation Workshops:", " Frame the problem, identify specific problem areas that Generative AI can address"],
      ["Use Case Identification:", " Identify set of use cases and define business goals for each use case."],
    ],
  },
  {
    icon: Database,
    image: "/assets/Data_Exploration.jpg",
    title: "Data Exploration and Collection",
    bullets: [
      ["Data Discovery and Evaluation:", " Identify and assess datasets relevant to your Gen AI project."],
      ["Data Acquisition:", " Gather high-quality data sources for training AI models."],
    ],
  },
  {
    icon: BrainCircuit,
    image: "/assets/AI_Models.jpg",
    title: "Model Selection",
    bullets: [
      ["Generative Model:", " Select from existing foundational models or design custom models suited to your goals."],
      ["Training and Optimization:", " Train AI models and fine-tune them for desired outcomes."],
    ],
  },
  {
    icon: Sparkles,
    image: "/assets/AI_Prompts.jpg",
    title: "Creative Outputs",
    bullets: [
      ["Prompt Engineering:", " Harness Generative AI prompts to produce creative content, such as art, music, or text."],
      ["Fine-Tune:", " Fine-tune for desired outcomes"],
      ["Evaluation:", " Assess the quality and relevance of generated outputs. Align with human feedback."],
    ],
  },
  {
    icon: Blocks,
    image: "/assets/Integration.jpg",
    title: "Integration and Deployment",
    bullets: [
      ["Integration Strategy:", " Plan how Generative AI fits into your existing systems or products."],
      ["Deployment and Scaling:", " Implement Gen AI solutions at scale with appropriate infrastructure."],
    ],
  },
  {
    icon: MonitorSmartphone,
    image: "/assets/UI.jpg",
    title: "User Experience Enhancement",
    bullets: [
      ["User Interface (UI) Design:", " Create interfaces that facilitate interaction with Gen AI outputs."],
      ["User Feedback Loop:", " Gather user feedback for continuous improvement."],
    ],
  },
  {
    icon: ShieldCheck,
    image: "/assets/Security.jpg",
    title: "Ethical Considerations",
    bullets: [
      ["Ethical AI Framework:", " Ensure that Gen AI aligns with ethical guidelines and avoids biases."],
      ["Privacy and Security Measures:", " Implement safeguards for data and AI-generated content."],
    ],
  },
  {
    icon: FlaskConical,
    image: "/assets/RND.jpg",
    title: "Continuous Innovation",
    bullets: [
      ["Research and Development:", " Stay at the forefront of Generative AI advancements."],
      ["Iterative Enhancements:", " Continuously improve Gen AI models and outputs."],
    ],
  },
  {
    icon: LifeBuoy,
    image: "/assets/support.jpg",
    title: "Support and Maintenance",
    bullets: [
      ["Technical Support:", " Provide assistance for users encountering issues with Gen AI."],
      ["Model Updates and Upgrades:", " Ensure that Gen AI models remain relevant and effective."],
    ],
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const GenerativeAI = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO {...seoConfig.generativeAI} jsonLd={[buildBreadcrumbJsonLd(seoConfig.generativeAI.crumbs)]} />
      <PageHero
        title="Generative AI Services"
        crumbs={[
          { label: "Home", href: "/" },
          { label: "Technology Services", href: "/services/technology-services" },
          { label: "Generative AI Services" },
        ]}
      />

      <section data-testid="genai-intro" className="pt-24 lg:pt-12 pb-24 lg:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
              <img
                src="/assets/Gen_AI-1.jpg"
                alt="Gen AI"
                className="w-full h-[360px] lg:h-[440px] object-cover"
              />
            </div>
          </motion.div>
          <motion.div {...fadeUp}>
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">
              Generative AI Services
            </span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl text-slate-900 leading-tight">
              Navigating the Future with You:{" "}
              <span className="text-miti-blue">Leading the Way in Gen AI,</span> Together
            </h2>
            <div className="mt-5 h-0.5 w-12 bg-miti-blue" />
            <p className="mt-6 text-base md:text-lg text-slate-500 leading-relaxed italic">
              "In the world of Generative AI, our journey begins with understanding your vision. Our experience and
              deep knowledge empower us to grasp your unique Gen AI aspirations and translate them into highly
              detailed and accurate business objective and project specifications. Together, we embark on a
              collaborative journey towards innovation, where your vision and our expertise merge to shape the
              future of AI for you."
            </p>
          </motion.div>
        </div>
      </section>

      <section data-testid="genai-offerings" className="pb-24 lg:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {offerings.map((o, i) => (
            <motion.div
              key={o.title}
              {...fadeUp}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6 }}
              data-testid={`genai-offering-${i + 1}`}
              className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-miti-blue/40 hover:shadow-xl hover:shadow-miti-blue/5 transition-all duration-300"
            >
              <div className="relative h-40 overflow-hidden">
                <img
                  src={o.image}
                  alt={o.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-miti-navy/70 via-miti-navy/5 to-transparent" />
                <div className="absolute bottom-3 left-3 w-11 h-11 rounded-xl bg-white/95 backdrop-blur text-miti-blue flex items-center justify-center shadow-lg">
                  <o.icon size={20} strokeWidth={1.8} />
                </div>
              </div>
              <div className="p-8">
                <h3 className="font-heading font-semibold text-lg text-slate-900 leading-snug">{o.title}</h3>
                <ul className="mt-4 space-y-3 list-disc pl-4 marker:text-miti-blue">
                  {o.bullets.map(([lead, rest], j) => (
                    <li key={j} className="text-sm text-slate-500 leading-relaxed">
                      <strong className="text-slate-800">{lead}</strong>
                      {rest}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section data-testid="genai-partner" className="relative py-28 lg:py-36 overflow-hidden">
        <img
          src="/assets/banner-1.jpg"
          alt="Transform your business with AI"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-miti-navy/75" />
        <motion.div {...fadeUp} className="relative max-w-3xl mx-auto px-6 text-center">
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-sky-300">
            Transform Your Business with AI
          </span>
          <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white">
            Partner with the Best in Generative AI
          </h2>
          <div className="mt-5 mx-auto h-0.5 w-12 bg-miti-blue" />
          <p className="mt-6 text-base md:text-lg text-slate-200 leading-relaxed">
            Leverage our expertise in generative AI to drive innovation and efficiency in your operations. Our team
            of skilled professionals consistently delivers high-quality, tailored AI solutions that transform
            businesses and unlock new opportunities. Experience excellence with every project we undertake.
          </p>
        </motion.div>
      </section>

      <section data-testid="genai-cta" className="py-24 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div {...fadeUp}>
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">Get in Touch</span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
              Ready to Transform Your Business?
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-500 leading-relaxed">
              Connect with our AI experts today and start leveraging the power of generative AI to drive innovation
              and efficiency in your operations. Whether you're exploring AI solutions or need detailed insights
              into our services, our team is here to help you succeed.
            </p>
            <a
              href="/contact"
              data-testid="genai-contact-button"
              className="group mt-9 inline-flex items-center gap-3 rounded-lg bg-miti-blue px-7 py-3.5 text-sm font-semibold text-white hover:bg-[#005a96] transition-colors"
            >
              <Handshake size={18} />
              Contact Us
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
              <img
                src="/assets/cta-bg.jpg"
                alt="Ready to Transform Your Business"
                className="w-full h-[320px] lg:h-[420px] object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default GenerativeAI;
